import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    getNextHighlightedIndexOnArrowKey,
    useDropdownPosition,
    useOutsideClick,
    useScrollHighlightedIntoView,
} from "../../hooks/useDropdownBehavior";
import {
    getVariant,
    sizes,
    StyledChevron,
    StyledClearButton,
    StyledDefaultInput,
    StyledDivider,
    StyledDropdownItem,
    StyledDropdownList,
    StyledEnd,
    StyledInfoIcon,
    StyledInputGroup,
    StyledLabel,
    StyledLeadingIcon,
    StyledMessage,
    StyledOptionContent,
    StyledOptionIcon,
    StyledOptionLabel,
    StyledRequired,
    StyledSelectValue,
    StyledSelectWrapper,
    StyledStateItem,
    StyledTop,
    StyledValueText,
} from "./SelectDropdown.styles";
import SearchableSelectDropdown from "./SearchableSelectDropdown";

/**
 * Standard AIE single-select dropdown. Supports a "filter" variant (table toolbars) and a
 * "bulk" variant (floating selection bars, opens upward since it's anchored near the bottom).
 * @param {Array<{value, label, icon?, disabled?}>} [props.options] Selectable options.
 * @param {(event: { target: { value, name }, selectedOption }) => void} [props.onChange] Also fired
 *   (with an empty value) by the clear button.
 * @param {(option: Object) => React.ReactNode} [props.renderOption] Custom option renderer.
 * @param {React.Ref} ref Forwarded to the select-value trigger element.
 * @param {string} [props.icon] Font Awesome icon name shown before the value/placeholder (e.g. "globe").
 * @param {string} [props.iconClass="fal"] Font Awesome style class paired with `icon`.
 * Also: placeholder, value, onSelect, onClear, onOpenChange, size, variant, disabled, disabledReason, loading, loadingMessage, emptyMessage/emptyText, error, label, required, info, helperText, errorText, clearable/isClearable, clearAriaLabel, name, id, ariaLabel, className (self-explanatory).
 */
const StaticSelectDropdown = forwardRef(function StaticSelectDropdown(
    {
        options = [],
        placeholder = "Select item",
        value,
        onChange,
        onSelect,
        onClear,
        onOpenChange,
        size = "lg",
        variant = "default",
        disabled = false,
        disabledReason,
        loading = false,
        loadingMessage = "Loading options...",
        emptyMessage,
        emptyText,
        error = false,
        label,
        required = false,
        info,
        helperText,
        errorText,
        clearable = false,
        isClearable = false,
        clearAriaLabel = "Clear selection",
        name = "",
        id,
        ariaLabel,
        className,
        renderOption,
        defaultValue,
        icon,
        iconClass = "fal",
        ...props
    },
    ref
) {
    const showClearButton = clearable || isClearable;

    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const containerRef = useRef(null);
    const listRef = useRef(null);
    const typeaheadRef = useRef({ search: "", timeoutId: null });

    const showError = Boolean(errorText || error);
    const selectedOption = options.find((option) => String(option.value) === String(value));
    const listboxId = id ? `${id}-listbox` : undefined;
    const message = errorText || helperText || (disabled ? disabledReason : undefined);
    const resolvedEmptyMessage = emptyMessage || emptyText || "No options available";
    const variantConfig = getVariant(variant);

    const setOpen = (nextOpen) => {
        setIsOpen(nextOpen);
        setHighlightedIndex(-1);

        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    };

    const emitChange = (nextValue, option = null) => {
        const target = { value: nextValue, name };

        if (onChange) {
            onChange({ target, currentTarget: target, selectedOption: option });
        }
    };

    const handleSelect = (option) => {
        if (disabled || (option && option.disabled)) {
            return;
        }

        emitChange(option.value, option);

        if (onSelect) {
            onSelect(option);
        }

        setOpen(false);
    };

    const handleClear = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (disabled) {
            return;
        }

        emitChange("", null);

        if (onClear) {
            onClear();
        }

        setOpen(false);
    };

    const TYPEAHEAD_RESET_MS = 500;

    const findTypeaheadMatch = (character) => {
        const buffer = typeaheadRef.current;

        clearTimeout(buffer.timeoutId);
        buffer.search += character.toLowerCase();
        buffer.timeoutId = setTimeout(() => {
            buffer.search = "";
        }, TYPEAHEAD_RESET_MS);

        const startIndex = highlightedIndex >= 0 ? highlightedIndex : -1;

        for (let count = 1; count <= options.length; count += 1) {
            const index = (startIndex + count + options.length) % options.length;
            const option = options[index];

            if (option && !option.disabled && String(option.label || "").toLowerCase().startsWith(buffer.search)) {
                return index;
            }
        }

        return -1;
    };

    const handleKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            if (isOpen && highlightedIndex >= 0 && options[highlightedIndex]) {
                handleSelect(options[highlightedIndex]);
            } else {
                setOpen(!isOpen);
            }

            return;
        }

        if (event.key === "Escape") {
            setOpen(false);
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();

            if (!isOpen) {
                setOpen(true);
            }

            setHighlightedIndex((current) => getNextHighlightedIndexOnArrowKey(options, current, event.key));

            return;
        }

        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const matchIndex = findTypeaheadMatch(event.key);

            if (matchIndex >= 0) {
                event.preventDefault();

                if (!isOpen) {
                    setOpen(true);
                }

                setHighlightedIndex(matchIndex);
            }
        }
    };

    useEffect(() => {
        setHighlightedIndex((current) => (current >= 0 && current < options.length ? current : -1));
    }, [options]);

    const menuPosition = useDropdownPosition(containerRef, isOpen, { dropUp: variantConfig.dropUp, autoFlip: true, listRef });

    useEffect(() => () => clearTimeout(typeaheadRef.current.timeoutId), []);

    useOutsideClick(containerRef, listRef, isOpen, () => setOpen(false));

    useScrollHighlightedIntoView(listRef, isOpen, highlightedIndex);

    const renderOptionContent = (option) => {
        if (renderOption) {
            return renderOption(option);
        }

        return (
            <StyledOptionContent>
                {option.icon && (
                    <StyledOptionIcon
                        className={`${option.iconClass || "fal"} fa-${option.icon}`}
                        $tone={option.iconTone || "default"}
                        aria-hidden="true"
                    />
                )}

                <StyledOptionLabel $emphasis={Boolean(option.emphasis)}>
                    {option.label}
                </StyledOptionLabel>
            </StyledOptionContent>
        );
    };

    return (
        <StyledInputGroup className={className}>
            <StyledDefaultInput>
                {label && (
                    <StyledTop>
                        <StyledLabel htmlFor={id}>
                            {label}
                            {required && <StyledRequired aria-hidden="true">*</StyledRequired>}
                        </StyledLabel>

                        {info && <StyledInfoIcon className="fal fa-info-circle" title={info} aria-label={info} />}
                    </StyledTop>
                )}

                <StyledSelectWrapper ref={containerRef}>
                    <StyledSelectValue
                        {...props}
                        ref={ref}
                        id={id}
                        $size={size}
                        $variant={variant}
                        $disabled={disabled}
                        $error={showError}
                        $open={isOpen}
                        role="combobox"
                        tabIndex={disabled ? -1 : 0}
                        aria-label={ariaLabel || label || placeholder}
                        aria-haspopup="listbox"
                        aria-expanded={isOpen}
                        aria-controls={listboxId}
                        aria-disabled={disabled || undefined}
                        aria-invalid={showError || undefined}
                        title={disabled ? disabledReason : undefined}
                        onClick={() => {
                            if (!disabled) {
                                setOpen(!isOpen);
                            }
                        }}
                        onKeyDown={handleKeyDown}
                    >
                        {icon && (
                            <StyledLeadingIcon $variant={variant} className={`${iconClass} fa-${icon}`} aria-hidden="true" />
                        )}

                        <StyledValueText $placeholder={!selectedOption} $variant={variant}>
                            {selectedOption ? selectedOption.label : placeholder}
                        </StyledValueText>

                        <StyledEnd $variant={variant}>
                            {showClearButton && selectedOption && !disabled && (
                                <StyledClearButton type="button" onClick={handleClear} aria-label={clearAriaLabel}>
                                    <i className="fal fa-times" aria-hidden="true" />
                                </StyledClearButton>
                            )}

                            {variantConfig.showDivider && <StyledDivider aria-hidden="true" />}

                            <StyledChevron $open={isOpen} $variant={variant} className="fal fa-chevron-down" aria-hidden="true" />
                        </StyledEnd>
                    </StyledSelectValue>

                    {isOpen && createPortal(
                        <StyledDropdownList
                            id={listboxId}
                            ref={listRef}
                            role="listbox"
                            $size={size}
                            $variant={variant}
                            $top={menuPosition.top}
                            $left={menuPosition.left}
                            $width={menuPosition.width}
                            $dropUp={menuPosition.dropUp}
                        >
                            {loading ? (
                                <StyledStateItem role="status">{loadingMessage}</StyledStateItem>
                            ) : options.length === 0 ? (
                                <StyledStateItem>{resolvedEmptyMessage}</StyledStateItem>
                            ) : (
                                options.map((option, index) => (
                                    <StyledDropdownItem
                                        key={option.value}
                                        role="option"
                                        aria-selected={String(option.value) === String(value)}
                                        aria-disabled={option.disabled || undefined}
                                        $selected={String(option.value) === String(value)}
                                        $highlighted={index === highlightedIndex}
                                        $disabled={Boolean(option.disabled)}
                                        title={option.disabled ? option.disabledReason : option.title}
                                        onClick={() => handleSelect(option)}
                                        onMouseEnter={() => {
                                            if (!option.disabled) {
                                                setHighlightedIndex(index);
                                            }
                                        }}
                                    >
                                        {renderOptionContent(option)}
                                    </StyledDropdownItem>
                                ))
                            )}
                        </StyledDropdownList>,
                        document.body
                    )}
                </StyledSelectWrapper>

                {message && <StyledMessage $error={Boolean(errorText)}>{message}</StyledMessage>}
            </StyledDefaultInput>
        </StyledInputGroup>
    );
});

/**
 * Renders the static-trigger dropdown above, or `SearchableSelectDropdown` when `searchable`.
 * @param {boolean} [props.searchable=false] Type in the field to filter options (the default
 *   variant only - `variant`, `icon` and `renderOption` aren't supported alongside it).
 * @param {React.Ref} ref Forwarded to the trigger (or search input) element.
 */
const SelectDropdown = forwardRef(function SelectDropdown({ searchable = false, defaultValue, ...props }, ref) {
    if (searchable) {
        return <SearchableSelectDropdown {...props} ref={ref} />;
    }

    return <StaticSelectDropdown {...props} defaultValue={defaultValue} ref={ref} />;
});

SelectDropdown.displayName = "SelectDropdown";

export default SelectDropdown;
